import { HttpClient } from '../api/HttpClient';
import { API_ENDPOINTS } from '../api/ApiConfig';
import type {
  AssignCastRequest,
  ContentCastResponse,
  CreateDramaRequest,
  Drama,
  DramaDetailResponse,
  DramasPage,
  DramasQueryParams,
  DramaCast,
  UpdateDramaRequest,
} from '../../types/api/drama';
import type {
  PreviewImageBatchRequest,
  PreviewImageResponse,
  PreviewImageUpdateRequest,
} from '../../types/api/movie';

/**
 * Drama / Series Service
 *
 * Handles all drama/series-related API operations for admin functionality.
 *
 * Features:
 * - Listing dramas/series with pagination and keyword filtering
 * - Drama/Series CRUD operations (create, update, soft delete, permanent delete)
 * - Optional cast management helpers
 *
 * NOTE:
 * - Backend uses a shared `Drama` aggregate for both DRAMA and SERIES.
 * - Series endpoints are the same as drama but require `dramaType = 'SERIES'`.
 */
export class DramaService {
  private httpClient: HttpClient;

  constructor() {
    this.httpClient = HttpClient.getInstance();
  }

  /**
   * Get paginated list of dramas/series.
   *
   * For series-only list, caller should pass `dramaType: 'series'` (lowercase)
   * as required by backend query param.
   */
  async getDramas(params: DramasQueryParams = {}): Promise<DramasPage> {
    const queryParams = new URLSearchParams();

    if (params.page !== undefined) {
      queryParams.append('page', params.page.toString());
    }
    if (params.size !== undefined) {
      queryParams.append('size', params.size.toString());
    }
    if (params.keyword) {
      queryParams.append('keyword', params.keyword);
    }
    if (params.dramaType) {
      queryParams.append('dramaType', params.dramaType);
    }
    if (params.accessLevel) {
      queryParams.append('accessLevel', params.accessLevel);
    }
    if (params.contentRegion) {
      queryParams.append('contentRegion', params.contentRegion);
    }

    const url = `${API_ENDPOINTS.DRAMAS.ADMIN_LIST}?${queryParams.toString()}`;
    const response = await this.httpClient.get<DramasPage>(url);

    if (!response.success) {
      throw new Error(response.message || 'Failed to fetch dramas');
    }

    return response.data;
  }

  /**
   * Get drama/series detail by ID.
   */
  async getDrama(dramaId: string): Promise<DramaDetailResponse> {
    const url = API_ENDPOINTS.DRAMAS.PUBLIC_DETAILS(dramaId);
    const response = await this.httpClient.get<DramaDetailResponse>(url);

    if (!response.success) {
      throw new Error(response.message || 'Failed to fetch drama');
    }

    return response.data;
  }

  /**
   * Create a new drama/series using multipart upload for thumbnail.
   *
   * The backend expects:
   * - `data`: JSON string (DramaCreateRequest)
   * - `thumbnail`: optional image file
   */
  async createDramaWithThumbnail(
    payload: CreateDramaRequest,
    thumbnailFile?: File
  ): Promise<Drama> {
    const formData = new FormData();

    // Ensure dramaType is always present in payload
    const data: CreateDramaRequest = {
      ...payload,
      dramaType: payload.dramaType,
    };

    const jsonBlob = new Blob([JSON.stringify(data)], {
      type: 'application/json',
    });
    formData.append('data', jsonBlob, 'data.json');

    if (thumbnailFile) {
      formData.append('thumbnail', thumbnailFile, thumbnailFile.name);
    }

    const response = await this.httpClient.request<Drama>({
      method: 'POST',
      url: API_ENDPOINTS.DRAMAS.ADMIN_CREATE_MULTIPART,
      data: formData,
      headers: {
        // HttpClient + RequestInterceptor are FormData-aware and will strip
        // Content-Type so axios/browser set correct multipart boundary.
        'Content-Type': 'multipart/form-data',
      },
    });

    if (!response.success) {
      throw new Error(response.message || 'Failed to create drama');
    }

    return response.data;
  }

  /**
   * Create a new drama/series using JSON-only endpoint (pre-uploaded thumbnail URL).
   */
  async createDramaWithJson(payload: CreateDramaRequest): Promise<Drama> {
    const response = await this.httpClient.post<Drama>(
      API_ENDPOINTS.DRAMAS.ADMIN_CREATE_JSON,
      payload
    );

    if (!response.success) {
      throw new Error(response.message || 'Failed to create drama');
    }

    return response.data;
  }

  /**
   * Update an existing drama/series.
   *
   * Uses JSON body and partial update semantics as per backend docs.
   */
  async updateDrama(dramaId: string, payload: UpdateDramaRequest): Promise<Drama> {
    const url = API_ENDPOINTS.DRAMAS.ADMIN_UPDATE(dramaId);
    const response = await this.httpClient.put<Drama>(url, payload);

    if (!response.success) {
      throw new Error(response.message || 'Failed to update drama');
    }

    return response.data;
  }

  /**
   * Soft delete a drama/series.
   *
   * Marks status = INACTIVE and performs cleanup on video assets.
   */
  async softDeleteDrama(dramaId: string): Promise<void> {
    const url = API_ENDPOINTS.DRAMAS.ADMIN_SOFT_DELETE(dramaId);
    const response = await this.httpClient.delete<{ message: string }>(url);

    if (!response.success) {
      throw new Error(response.message || 'Failed to delete drama');
    }
  }

  /**
   * Permanently delete a drama/series.
   *
   * SUPER_ADMIN only. Cascades through all related entities and storage keys.
   */
  async permanentDeleteDrama(dramaId: string): Promise<void> {
    const url = API_ENDPOINTS.DRAMAS.ADMIN_PERMANENT_DELETE(dramaId);
    const response = await this.httpClient.delete<{ message: string }>(url);

    if (!response.success) {
      throw new Error(response.message || 'Failed to permanently delete drama');
    }
  }

  /**
   * Assign a cast member to a drama/series.
   */
  async assignCast(dramaId: string, payload: AssignCastRequest): Promise<ContentCastResponse> {
    const url = API_ENDPOINTS.DRAMAS.ADMIN_ASSIGN_CAST(dramaId);
    const response = await this.httpClient.post<ContentCastResponse>(url, payload);

    if (!response.success) {
      throw new Error(response.message || 'Failed to assign cast');
    }

    return response.data;
  }

  /**
   * Remove a cast member from a drama/series.
   */
  async removeCast(dramaId: string, castId: string): Promise<void> {
    const url = API_ENDPOINTS.DRAMAS.ADMIN_REMOVE_CAST(dramaId, castId);
    const response = await this.httpClient.delete<{ message: string }>(url);

    if (!response.success) {
      throw new Error(response.message || 'Failed to remove cast');
    }
  }

  /**
   * Update the role of an existing cast member on a drama/series.
   */
  async updateCastRole(dramaId: string, castId: string, role: string): Promise<ContentCastResponse> {
    const baseUrl = API_ENDPOINTS.DRAMAS.ADMIN_UPDATE_CAST_ROLE(dramaId, castId);
    const url = `${baseUrl}?role=${encodeURIComponent(role)}`;

    const response = await this.httpClient.put<ContentCastResponse>(url, undefined);

    if (!response.success) {
      throw new Error(response.message || 'Failed to update cast role');
    }

    return response.data;
  }

  /**
   * List all casts for a given drama/series.
   */
  async getCasts(dramaId: string): Promise<DramaCast[]> {
    const url = API_ENDPOINTS.DRAMAS.PUBLIC_LIST_CASTS(dramaId);
    const response = await this.httpClient.get<DramaCast[]>(url);

    if (!response.success) {
      throw new Error(response.message || 'Failed to fetch casts');
    }

    return response.data;
  }

  /**
   * Upload preview images for a drama/series (batch, up to 5 per request).
   *
   * Backend endpoint: POST /api/admin/dramas/{dramaId}/preview-images
   * Expects multipart/form-data with:
   *  - files: MultipartFile[] (required, 1..5)
   *  - metadata: JSON(PreviewImageBatchRequest) (optional)
   */
  async uploadPreviewImages(
    dramaId: string,
    files: File[],
    metadata?: PreviewImageBatchRequest
  ): Promise<PreviewImageResponse[]> {
    const formData = new FormData();

    files.forEach((file) => {
      formData.append('files', file, file.name);
    });

    if (metadata && metadata.metadata && metadata.metadata.length > 0) {
      const metadataBlob = new Blob([JSON.stringify(metadata)], {
        type: 'application/json',
      });
      formData.append('metadata', metadataBlob, 'metadata.json');
    }

    const response = await this.httpClient.request<PreviewImageResponse[]>({
      method: 'POST',
      url: `/admin/dramas/${dramaId}/preview-images`,
      data: formData,
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    });

    if (!response.success) {
      throw new Error(response.message || 'Failed to upload preview images');
    }

    return response.data;
  }

  /**
   * Update preview image metadata for a drama/series (JSON only).
   *
   * Backend endpoint: PUT /api/admin/dramas/{dramaId}/preview-images/{previewImageId}
   * Content-Type: application/json
   */
  async updatePreviewImageMetadata(
    dramaId: string,
    previewImageId: string,
    payload: PreviewImageUpdateRequest
  ): Promise<PreviewImageResponse> {
    const response = await this.httpClient.put<PreviewImageResponse>(
      `/admin/dramas/${dramaId}/preview-images/${previewImageId}`,
      payload
    );

    if (!response.success) {
      throw new Error(response.message || 'Failed to update preview image metadata');
    }

    return response.data;
  }

  /**
   * Update preview image metadata with optional file replacement for a drama/series.
   *
   * Backend endpoint: PUT /api/admin/dramas/{dramaId}/preview-images/{previewImageId}
   * Content-Type: multipart/form-data
   * Parts:
   *  - data: JSON (PreviewImageUpdateRequest) (required)
   *  - file: MultipartFile (optional)
   */
  async updatePreviewImageWithFile(
    dramaId: string,
    previewImageId: string,
    payload: PreviewImageUpdateRequest,
    file?: File
  ): Promise<PreviewImageResponse> {
    const formData = new FormData();

    const dataBlob = new Blob([JSON.stringify(payload)], {
      type: 'application/json',
    });
    formData.append('data', dataBlob, 'data.json');

    if (file) {
      formData.append('file', file, file.name);
    }

    const response = await this.httpClient.request<PreviewImageResponse>({
      method: 'PUT',
      url: `/admin/dramas/${dramaId}/preview-images/${previewImageId}`,
      data: formData,
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    });

    if (!response.success) {
      throw new Error(response.message || 'Failed to update preview image');
    }

    return response.data;
  }
}


